import type { MouseEventHandler } from 'react'
import { useState } from 'react'

import LaunchIcon from '@mui/icons-material/Launch'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'

import { Actions } from '@components/Actions'
import { ProjectAdres } from '@components/Adres'
import { MyListButton } from '@components/MyListButton'
import { PlanningsverschilIcon } from '@components/PlanningsverschilIcon'
import Realisatie from '@components/Realisatie'
import { Status } from '@components/Status'
import { formatDate } from '@util/date-util'

import ExpandRow from '../../../theme/components/ExpandRow'
import { Label } from '../../../theme/components/Label'
import { StyledIconLink } from '../../../theme/components/StyledIconLink'
import type { Project } from '../../project/types/Project'
import type { ProjectenSearchFilters as Filters } from '../types/ProjectenSearch'

import { AansluitObjecten } from './AansluitObjecten'
import { DisciplineVerzamelingen } from './DisciplineVerzamelingen'

interface Props {
  project: Project
  onClickMenu: any
  showActions: boolean
  filters: Filters
}

export const ProjectenTableRow = ({
  project,
  onClickMenu,
  showActions,
  filters,
}: Props) => {
  const [expandObject, setExpandObject] = useState(false)
  const [expandDiscipline, setExpandDiscipline] = useState(false)

  const handleObjectenClick = () => {
    setExpandDiscipline(false)
    setExpandObject(!expandObject)
  }

  const handleDisciplinesClick = () => {
    setExpandObject(false)
    setExpandDiscipline(!expandDiscipline)
  }

  const handleMenu: MouseEventHandler<HTMLButtonElement> = (event) => {
    onClickMenu(event.currentTarget, project)
  }

  return (
    <>
      <TableRow key={project.aanvraagID}>
        <TableCell>
          <Box sx={{ pl: '22px' }}>
            <Status project={project} />
          </Box>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
            }}
          >
            {project.aanvraagID}
            {project.labels && project.labels.length > 0 && (
              <div>
                {project.labels.map((l) => (
                  <Label text={l} key={l} small />
                ))}
              </div>
            )}
          </Box>
        </TableCell>
        <TableCell>{formatDate(project.aanvraagDatum)}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Realisatie
              date={project.geplandeUitvoeringsdatum as string}
              type={project.geplandeUitvoeringsdatumType}
              wachtTijdDatum={project.wachtTijdDatum}
            />
            {project.planningsverschillen?.map((pv) => (
              <PlanningsverschilIcon
                key={pv}
                context="project"
                planningsverschil={pv}
              />
            ))}
          </Box>
        </TableCell>
        <TableCell>
          <ProjectAdres {...project} />
        </TableCell>
        {showActions ? (
          <TableCell colSpan={2}>
            {project.type === 'Aanvraag' && (
              <Actions
                acties={project.acties}
                selectedActie={filters.actie as any}
                selectedActiehouder={filters.actiehouder as any}
              />
            )}
          </TableCell>
        ) : (
          <>
            <TableCell>
              <ExpandRow
                data-testid="chipObjecten"
                label={project.aantalObjecten.toString()}
                expanded={expandObject}
                onDelete={handleObjectenClick}
                onClick={handleObjectenClick}
              />
            </TableCell>
            <TableCell>
              <ExpandRow
                data-testid="chipDisciplines"
                label={project.uniqDisciplines.toString()}
                expanded={expandDiscipline}
                onDelete={handleDisciplinesClick}
                onClick={handleDisciplinesClick}
              />
            </TableCell>
          </>
        )}
        <TableCell>{project.gebiedscodeLabel}</TableCell>
        <TableCell>{project.statusText}</TableCell>
        <TableCell>
          <MyListButton
            active={project.myList}
            type="aansluiting"
            aanvraagID={project.aanvraagID}
          />

          <StyledIconLink
            href={`/aansluiting/project/${project.aanvraagID}`}
            icon={<LaunchIcon />}
            title={`Navigeer naar project ${project.aanvraagID}`}
          />

          <IconButton data-testid="buttonMenu" onClick={handleMenu}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {expandDiscipline ? <DisciplineVerzamelingen project={project} /> : null}
      {expandObject ? <AansluitObjecten project={project} /> : null}
    </>
  )
}
